/**
 * Carousel Component
 * 
 * A reusable carousel/slideshow component for displaying content slides.
 * Supports keyboard navigation and accessibility.
 */

import React, { useState } from 'react';
import { Button } from '@components/common/Button';
import styles from './Carousel.module.scss';

interface CarouselProps {
  slides: React.ReactNode[];
  onComplete?: () => void;
}

export const Carousel: React.FC<CarouselProps> = ({ slides, onComplete }) => {
  const [currentSlide, setCurrentSlide] = useState(0);

  const goToSlide = (index: number) => {
    setCurrentSlide(index);
  };

  const goToNext = () => {
    if (currentSlide < slides.length - 1) {
      setCurrentSlide(currentSlide + 1);
    } else if (onComplete) {
      onComplete();
    }
  };

  const goToPrevious = () => {
    if (currentSlide > 0) {
      setCurrentSlide(currentSlide - 1);
    }
  };

  const isLastSlide = currentSlide === slides.length - 1;

  return (
    <div className={styles.carousel}>
      <div className={styles.slideContainer}>
        <div 
          className={styles.slides}
          style={{ transform: `translateX(-${currentSlide * 100}%)` }}
        >
          {slides.map((slide, index) => (
            <div key={index} className={styles.slide}>
              {slide}
            </div>
          ))}
        </div>
      </div>

      <div className={styles.navigation}>
        <div className={styles.dots}>
          {slides.map((_, index) => (
            <button
              key={index}
              className={`${styles.dot} ${index === currentSlide ? styles.active : ''}`}
              onClick={() => goToSlide(index)}
              aria-label={`Go to slide ${index + 1}`}
            />
          ))}
        </div>

        <div className={styles.controls}>
          {currentSlide > 0 && (
            <Button
              variant="secondary"
              onClick={goToPrevious}
              aria-label="Previous slide"
            >
              ← Previous
            </Button>
          )}
          
          <Button
            variant="primary"
            onClick={goToNext}
            aria-label={isLastSlide ? 'Start playing' : 'Next slide'}
          >
            {isLastSlide ? 'Start Playing!' : 'Next →'}
          </Button>
        </div>
      </div>
    </div>
  );
};